import { useCallback, useState } from 'react';
import { API_URL } from '../httpClient';
import fileApi from '../services/file.service';
import type { Message, MessageAttachment } from '../types/chat.types';

/**
 * Attachment for sending
 */
export interface SendAttachment {
  file: File;
  type: 'image' | 'video' | 'audio' | 'document';
}

/**
 * useSendMessage return type
 */
export interface UseSendMessageReturn {
  /** Send a message with optional attachments */
  sendMessage: (
    conversationId: number,
    body: string,
    attachments?: SendAttachment[],
    quotedMessageId?: number,
  ) => Promise<Message | null>;
  /** Whether currently sending */
  isLoading: boolean;
  /** Error from last send attempt */
  error: Error | null;
  /** Clear the error state */
  clearError: () => void;
  /** Upload progress (0-100) */
  uploadProgress: number;
}

/**
 * Hook for sending messages.
 * Handles message creation and file uploads.
 *
 * @returns Send message function and status
 *
 * @example
 * ```tsx
 * function MessageInput({ conversationUuid }: { conversationUuid: string }) {
 *   const [text, setText] = useState('');
 *   const [files, setFiles] = useState<File[]>([]);
 *   const { sendMessage, isLoading, error } = useSendMessage();
 *
 *   const handleSend = async () => {
 *     const attachments = files.map(file => ({
 *       file,
 *       type: file.type.startsWith('image/') ? 'image' : 'document',
 *     }));
 *
 *     const message = await sendMessage(conversationUuid, text, attachments);
 *     if (message) {
 *       setText('');
 *       setFiles([]);
 *     }
 *   };
 *
 *   return (
 *     <div>
 *       <input value={text} onChange={e => setText(e.target.value)} />
 *       <input type="file" onChange={e => setFiles([...e.target.files])} />
 *       <button onClick={handleSend} disabled={isLoading}>
 *         {isLoading ? 'Sending...' : 'Send'}
 *       </button>
 *       {error && <span className="error">{error.message}</span>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useSendMessage(): UseSendMessageReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const uploadFile = useCallback(
    async (
      file: File,
      onProgress?: (percent: number) => void
    ): Promise<{ fileId: number; fileName: string; mimeType: string; fileSize: number }> => {
      // Use the presigned URL upload flow from fileApi
      const result = await fileApi.uploadFile(file, {
        acl: 'private',
        onProgress: onProgress
          ? (progress) => onProgress(progress.percent)
          : undefined,
      });

      return {
        fileId: result.id,
        fileName: result.filename,
        mimeType: file.type,
        fileSize: file.size,
      };
    },
    []
  );

  const sendMessage = useCallback(
    async (
      conversationId: number,
      body: string,
      attachments?: SendAttachment[],
      quotedMessageId?: number,
    ): Promise<Message | null> => {
      setIsLoading(true);
      setError(null);
      setUploadProgress(0);

      try {
        const token = localStorage.getItem('token');
        const tenantUuid = localStorage.getItem('tenantUuid');

        if (!token || !tenantUuid) {
          throw new Error('Not authenticated');
        }

        // Upload attachments if any
        let uploadedAttachments: Partial<MessageAttachment>[] = [];
        if (attachments && attachments.length > 0) {
          const totalFiles = attachments.length;
          let uploadedCount = 0;

          uploadedAttachments = await Promise.all(
            attachments.map(async (attachment) => {
              const result = await uploadFile(attachment.file, (percent) => {
                // Track individual file progress
                const baseProgress = (uploadedCount / totalFiles) * 100;
                const fileProgress = (percent / totalFiles);
                setUploadProgress(Math.round(baseProgress + fileProgress));
              });
              uploadedCount++;
              setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
              return {
                fileType: attachment.type,
                fileId: result.fileId,
                fileName: result.fileName,
                mimeType: result.mimeType,
                fileSize: result.fileSize,
              };
            })
          );
        }

        // Determine message type
        const messageType =
          uploadedAttachments.length > 0 ? uploadedAttachments[0].fileType : 'text';

        // Send message
        const response = await fetch(`${API_URL}/message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-tenant-user-auth': token,
            'x-tenant-uuid': tenantUuid,
          },
          body: JSON.stringify({
            conversationId,
            type: messageType,
            body: body || undefined,
            attachments: uploadedAttachments.length > 0 ? uploadedAttachments : undefined,
            quotedMessageId: quotedMessageId || undefined,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to send message: ${response.statusText}`);
        }

        const result = await response.json();
        return result.data as Message;
      } catch (err) {
        const error = err instanceof Error ? err : new Error('Unknown error');
        setError(error);
        return null;
      } finally {
        setIsLoading(false);
        setUploadProgress(0);
      }
    },
    [uploadFile]
  );

  return {
    sendMessage,
    isLoading,
    error,
    clearError,
    uploadProgress,
  };
}

/**
 * useRetryMessage return type
 */
export interface UseRetryMessageReturn {
  /** Retry a failed message by its ID */
  retry: (messageId: number) => Promise<Message | null>;
  /** ID of the message currently being retried (null if none) */
  retryingMessageId: number | null;
}

/**
 * Hook for retrying failed messages.
 * Calls POST /message/:messageId/retry.
 * Status updates arrive via WebSocket (chat:message-status).
 */
export function useRetryMessage(): UseRetryMessageReturn {
  const [retryingMessageId, setRetryingMessageId] = useState<number | null>(null);

  const retry = useCallback(async (messageId: number): Promise<Message | null> => {
    setRetryingMessageId(messageId);
    try {
      const token = localStorage.getItem('token');
      const tenantUuid = localStorage.getItem('tenantUuid');

      if (!token || !tenantUuid) {
        throw new Error('Not authenticated');
      }

      const response = await fetch(`${API_URL}/message/${messageId}/retry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-tenant-user-auth': token,
          'x-tenant-uuid': tenantUuid,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to retry message: ${response.statusText}`);
      }

      const result = await response.json();
      return result.data as Message;
    } catch {
      return null;
    } finally {
      setRetryingMessageId(null);
    }
  }, []);

  return { retry, retryingMessageId };
}
